import { HttpClient } from '../HttpClient';
/**
 * Get a paginated list of companies with employee viewing capability.
 *
 * @param {string} searchString - (Optional) Search string to filter companies by name.
 * @param {number} [pageNumber=1] - (Optional) The page number for pagination.
 * @param {number} [rowsPerPage=10] - (Optional) Number of items per page.
 * @param {string} [sortColumn='companyName'] - (Optional) Column to sort by.
 * @param {string} [sortOrder='asc'] - (Optional) Sort order ('asc' or 'desc').
 * @returns {Promise<{
 *   paginatedList: {
 *     data: Array<{ id: number, companyName: string }>,
 *     pagination: {
 *       currentPage: number,
 *       totalPages: number,
 *       totalItems: number,
 *       itemsPerPage: number,
 *       links: {
 *         next?: string,
 *         last?: string
 *       }
 *     }
 *   },
 *   defaultCompanyId: number,
 *   defaultCompanyName: string
 * }>} Resolves with paginated company data and default company info.
 * @throws Will reject with an error if the request fails.
 */

/**
 * Updated to accept a single options object for easier parameter handling.
 *
 * @param {Object} options - Options for filtering and pagination.
 * @param {string} [options.searchString] - (Optional) Search string to filter companies by name.
 * @param {number} [options.pageNumber=1] - (Optional) The page number for pagination.
 * @param {number} [options.rowsPerPage=10] - (Optional) Number of items per page.
 * @param {string} [options.sortColumn='companyName'] - (Optional) Column to sort by.
 * @param {string} [options.sortOrder='asc'] - (Optional) Sort order ('asc' or 'desc').
 * @returns {Promise<{
 *   paginatedList: {
 *     data: Array<{ id: number, companyName: string }>,
 *     pagination: {
 *       currentPage: number,
 *       totalPages: number,
 *       totalItems: number,
 *       itemsPerPage: number,
 *       links: {
 *         next?: string,
 *         last?: string
 *       }
 *     }
 *   },
 *   defaultCompanyId: number,
 *   defaultCompanyName: string
 * }>} Resolves with paginated company data and default company info.
 * @throws Will reject with an error if the request fails.
 */
export const GetCompanyWithEmployeeViewing = async (
  searchString,
  pageNumber = 1,
  rowsPerPage = 10,
  sortColumn = 'companyName',
  sortOrder = 'asc',
) => {
  try {
    const { data } = await HttpClient.get('api/companies/filter', {
      params: {
        pageNumber: pageNumber,
        rowsPerPage: rowsPerPage,
        ...(searchString ? { searchString: searchString } : {}),
        ...(sortColumn ? { sortColumn: sortColumn } : {}),
        ...(sortOrder ? { sortOrder: sortOrder } : {}),
      },
    });
    return await Promise.resolve(data);
  } catch (err) {
    return Promise.reject(err);
  }
};
